import React, { useEffect, useRef, useState } from 'react';
import { Container } from '@/components/Containers/Container';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Filter } from '@/components/Filter/Filter';
import { Typography } from '@/components/Typography/Typography';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { clsx } from 'clsx';
import { useInView } from 'framer-motion';
import Image from 'next/image';

const Circle = () => {
  return (
    <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="9.5" r="9" fill="#05050D" />
      <path d="M6.78454 13.097L10.3816 9.5L6.78454 5.90296" stroke="white" strokeWidth="2" />
    </svg>
  );
};
const filterContent = [
  {
    id: '0',
    activeColor: 'data-[state=active]:bg-green-default data-[state=active]:text-ebony',
    text: (
      <div className="flex items-center justify-center gap-5">
        <Typography $type="PSmall" $weight="800" className="font-formula">
          Open your Freshdesk ticket
        </Typography>
        <Circle />
      </div>
    ),
  },
  {
    id: '1',
    activeColor: 'data-[state=active]:bg-green-default data-[state=active]:text-ebony',
    text: (
      <div className="flex items-center justify-center gap-5">
        <Typography $type="PSmall" $weight="800" className="font-formula">
          Select CoSupport™ Agent suggestion
        </Typography>
        <Circle />
      </div>
    ),
  },
  {
    id: '2',
    activeColor: 'data-[state=active]:bg-green-default data-[state=active]:text-ebony',
    text: (
      <div className="flex items-center justify-center gap-5">
        <Typography $type="PSmall" $weight="800" className="font-formula">
          Done
        </Typography>
      </div>
    ),
  },
];

const desktopItems = [
  <>
    <Image
      alt="easy-start-with-freshdesk-desktop-1"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-desktop-1.webp"
      width="2048"
      height="1556"
    />
  </>,
  <>
    <Image
      alt="easy-start-with-freshdesk-desktop-2"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-desktop-2.webp"
      width="2048"
      height="1556"
    />
  </>,
  <>
    <Image
      alt="easy-start-with-freshdesk-desktop-3"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-desktop-3.webp"
      width="2048"
      height="1556"
    />
  </>,
];

const mobileItems = [
  <div key="first-slide">
    <Image
      alt="easy-start-with-freshdesk-mobile-1"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-mobile-1.webp"
      width="984"
      height="1024"
    />
  </div>,
  <div key="second-slide">
    <Image
      alt="easy-start-with-freshdesk-mobile-2"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-mobile-2.webp"
      width="984"
      height="1024"
    />
  </div>,
  <div key="last-slide">
    <Image
      alt="easy-start-with-freshdesk-mobile-3"
      src="/img/freshdesk-integration/easy-start-with-freshdesk/easy-start-with-freshdesk-mobile-3.webp"
      width="984"
      height="1024"
    />
  </div>,
];

const titles = [
  <>Open your Freshdesk ticket</>,
  <>Select CoSupport™ Agent suggestion</>,
  <>Done</>,
];

export const EasyStartWithFreshdesk = () => {
  const isLgUp = useMediaQuery('(min-width: 1024px)');

  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-300px' });

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const plugin = useRef(Autoplay({ delay: 4000 }));

  const handleFilterChange = (value: string | number) => {
    setCurrent(Number(value) + 1);
    plugin.current.stop();
  };

  useEffect(() => {
    if (isInView) {
      if (!plugin.current.isPlaying()) {
        plugin.current.play();
      }
    } else {
      plugin.current.stop();
    }
  }, [isInView]);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    if (api) {
      api.scrollTo(current - 1);
    }
  }, [current, api]);

  return (
    <section
      className={clsx(
        'relative bg-light max-lg:bg-gradient_malibu md:pb-24.5-vw pb-12.5 md:pt-7.5-vw h-full'
      )}
    >
      <Container>
        <div className="relative">
          <div className="z-10 flex flex-col justify-center items-center lg:gap-7.5-vw gap-6 relative">
            <div className="bg-gradient_malibu flex flex-col lg:gap-7.5-vw gap-7 items-start w-full md:rounded-lg-20-vw rounded-md-10 z-10 lg:pr-24.5-vw lg:pl-24.5-vw">
              <div className="lg:max-w-[59.444vw] lg:mt-17-vw mt-11">
                <Typography as="h2" $weight="700" $type="Heading1" className="font-formula">
                  And Enhance Agent productivity with AI Assistance for Freshdesk
                </Typography>
              </div>
              <div className="flex flex-col lg:gap-12.5-vw w-full items-start">
                {isLgUp ? (
                  <>
                    <Filter
                      classNameTabsList="w-full flex-nowrap [&>button]:w-full lg:[&>button]:h-[2.569vw]"
                      className="w-full"
                      containerClassName="w-full lg:max-w-fit"
                      onValueChange={handleFilterChange}
                      filterContent={filterContent}
                      current={(current - 1).toString()}
                    />
                    <Carousel
                      plugins={[plugin.current]}
                      setApi={setApi}
                      className="w-full h-full [&>_.carousel-parent-content]:h-full"
                    >
                      <CarouselContent className="h-full">
                        {desktopItems.map((slide, index, array) => (
                          <CarouselItem key={`${slide}-${index}`} className="h-full">
                            {slide}
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                  </>
                ) : (
                  <div className="h-full w-full relative">
                    <div className="flex flex-col items-start md:gap-4-vw gap-4 absolute top-9 left-0">
                      <div className="flex gap-4 z-50 justify-start text-start text-sm text-muted-foreground">
                        {[1, 2, 3].map((slide, index) => (
                          <div
                            onClick={() => api?.scrollTo(index)}
                            key={`${slide}`}
                            className={clsx(
                              'h-[9px] w-[9px] rounded-full cursor-pointer',
                              slide !== current && 'opacity-40',
                              'bg-ebony'
                            )}
                          />
                        ))}
                      </div>
                    </div>
                    <Carousel
                      plugins={[plugin.current]}
                      setApi={setApi}
                      className="w-full h-full [&>_.carousel-parent-content]:h-full"
                    >
                      <CarouselContent className="h-full">
                        {mobileItems.map((slide, index) => (
                          <CarouselItem
                            key={`${slide}-${index}`}
                            className="h-full w-full flex flex-col gap-5 items-center justify-center"
                          >
                            <Typography
                              as="p"
                              $type="PSmall"
                              $weight="700"
                              className="text-start font-formula opacity-50 w-full mb-6"
                            >
                              {titles[index]}
                            </Typography>
                            {slide}
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </Carousel>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
